import React, {ReactNode} from "react";
import {isJunkFolder, isOutgoingFolder} from "../../reducers/FolderReducer";
import {DropDownMenuItem} from "../toolbar/DropDownMenu";
import {EmailHeader} from "../../services/messages/EmailHeaderDto";
import {EmailActions} from "../../actions/EmailActions";
import DraftsIcon from '@mui/icons-material/Drafts';
import FlagIcon from '@mui/icons-material/Flag';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplyIcon from '@mui/icons-material/Reply';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import ForwardIcon from '@mui/icons-material/Forward';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import {EmailLocation, handleViewEmail} from "../../locations/EmailLocation";
import {isEmailContent} from "../../domain/ItemContent";
import {useAppDispatch, useAppSelector} from "../../hooks/ReduxHooks";
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import {Divider, Menu, MenuItem} from "@mui/material";
import {ContextMenuEvent} from "../../util/TypeHelper";
import {getItemContent} from "../../state/ItemState";
import {AnyAction} from "redux";
import {EmailResponseType} from "../../formatters/EmailResponseFormatter";

interface EmailContextMenuProps {
    anchorEl: Element | null;
    onClose: () => void;
}

const RenderEmailContextMenu = ({
                                    anchorEl,
                                    onClose,
                                }: EmailContextMenuProps) => {

    const selectedFolder = useAppSelector(s => s.folderState.selectedFolder);

    const selectedEmailHeaders = useAppSelector(s => s.itemState.selectedEmailHeaders);

    const hasReadEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => e.Read));
    const hasUnreadEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => !e.Read));

    const hasFlaggedEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => e.Flagged));
    const hasUnflaggedEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => !e.Flagged));

    const isOutgoing = isOutgoingFolder(selectedFolder?.type);
    const isJunk = isJunkFolder(selectedFolder?.icon);

    const haveEmailContent = useAppSelector(s => {
        const content = getItemContent(s.itemState);
        return isEmailContent(content) && selectedEmailHeaders[0]?.Id === content.Id;
    });

    const hasPinnedEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => !!e.Pinned));
    const hasUnpinnedEmails = useAppSelector(s => s.itemState.selectedEmailHeaders.some(e => !e.Pinned));

    const plural = selectedEmailHeaders.length > 1 ? "s" : "";

    const noEmails = selectedEmailHeaders.length === 0;

    const dispatch = useAppDispatch();

    const onMarkAsRead = async (read: boolean) => await EmailActions.markEmailsAsRead(dispatch, selectedEmailHeaders, read);
    const onSetFlag = async (emails: EmailHeader[], flag: boolean) => await EmailActions.setEmailFlags(dispatch, emails, flag);
    const onSetPinned = async (emails: EmailHeader[], pinned: boolean) => await EmailActions.setEmailPinned(dispatch, emails, pinned);
    const onDeleteEmails = async (emails: EmailHeader[]) => await EmailActions.deleteEmails(dispatch, emails);

    const canRespond = haveEmailContent && selectedEmailHeaders.length === 1;

    function createContextMenuItem(icon: ReactNode, name: string, hide: boolean, onClick: () => Promise<any> | AnyAction, disabled?: boolean): DropDownMenuItem {
        return {
            icon,
            name,
            hide,
            onClick,
            disabled,
        };
    }

    function onRespondToEmail(responseType: EmailResponseType) {
        return handleViewEmail(selectedEmailHeaders[0].Id, dispatch, itemContent => EmailLocation.respondToEmail(dispatch, itemContent, responseType));
    }

    const contextMenuItem: (DropDownMenuItem | string)[] = [
        createContextMenuItem(<DraftsIcon/>, "Mark as Read", !hasUnreadEmails || isOutgoing, () => onMarkAsRead(true)),
        createContextMenuItem(<MarkunreadIcon/>, "Mark as Unread", !hasReadEmails || isOutgoing, () => onMarkAsRead(false)),

        createContextMenuItem(<FlagIcon/>, "Flag Email" + plural, !hasUnflaggedEmails || isOutgoing, () => onSetFlag(selectedEmailHeaders, true)),
        createContextMenuItem(<FlagIcon/>, "Remove Flag" + plural, !hasFlaggedEmails || isOutgoing, () => onSetFlag(selectedEmailHeaders, false)),

        createContextMenuItem(<PushPinIcon/>, "Pin Email" + plural, !hasUnpinnedEmails, () => onSetPinned(selectedEmailHeaders, true)),
        createContextMenuItem(<PushPinOutlined/>, "Unpin Email" + plural, !hasPinnedEmails, () => onSetPinned(selectedEmailHeaders, false)),

        createContextMenuItem(<AssignmentReturnIcon/>, "Move Email" + plural, noEmails || isOutgoing, () => dispatch(EmailActions.beginMoveEmails(selectedEmailHeaders))),
        createContextMenuItem(<NotificationsPausedIcon/>, "Remind Me Later", selectedEmailHeaders.length !== 1 || isOutgoing || !haveEmailContent, () => dispatch(EmailActions.showRemindMeLaterSettingsDialog(true))),

        canRespond ? "div1" : null!,

        createContextMenuItem(<ReplyIcon/>, "Reply", !canRespond, () => onRespondToEmail("Reply"), noEmails),
        createContextMenuItem(<ReplyAllIcon/>, "Reply All", !canRespond, () => onRespondToEmail("ReplyAll"), noEmails),
        createContextMenuItem(<ForwardIcon/>, "Forward", !canRespond, () => onRespondToEmail("Forward"), noEmails),

        "div2",

        createContextMenuItem(<DeleteIcon/>, "Delete", false, () => onDeleteEmails(selectedEmailHeaders), noEmails),

        createContextMenuItem(isJunk ? <CheckCircleIcon/> : <BlockIcon/>, isJunk ? "Mark as not Junk" : "Mark as Junk", isOutgoing, () => EmailActions.markEmailsAsJunk(dispatch, selectedEmailHeaders, selectedFolder), noEmails),
    ];


    function handleContextMenuItemClick(_: ContextMenuEvent<HTMLLIElement>, menuItem: DropDownMenuItem) {
        onClose();

        menuItem.onClick();
    }

    // TODO: use the mouse position for the context menu
    return (
        <Menu anchorEl={anchorEl}
              onClose={onClose}
              open={Boolean(anchorEl)}
              keepMounted
              anchorReference="anchorEl"
              componentsProps={{backdrop: {onContextMenu: event => event.preventDefault()}}}
              onContextMenu={event => event.preventDefault()}
              transitionDuration={100}
            // anchorPosition={
            //     contextPosition.x !== null && contextPosition.y !== null
            //         ? {top: contextPosition.y, left: contextPosition.x}
            //         : undefined
            // }
        >
            {contextMenuItem
                .filter(mi => mi && (typeof mi === "string" || !mi.hide))
                .map(menuItem => (
                    (typeof menuItem === "string")
                        ? (<Divider key={menuItem}/>) :
                        (<MenuItem key={menuItem.name}
                                   onClick={(event) => handleContextMenuItemClick(event, menuItem)}
                                   disabled={menuItem.disabled}>
                            {menuItem.icon}
                            <span className="popup-text">{menuItem.name}</span>
                        </MenuItem>)
                ))}
        </Menu>
    );
};

export const EmailContextMenu = RenderEmailContextMenu;
